import { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { isNativeRuntime } from "@/lib/nativeRuntime";
import { Button } from "@/components/ui/button";
import { Loader2, Wifi, WifiOff } from "lucide-react";
import { App as CapacitorApp } from "@capacitor/app";

// FIX: Increased from 8000 to 15000. The native WebView fetches the app from
// a remote URL — JS bundle download and parse consume several seconds before
// Boot.tsx even runs. 8s left almost no budget for the actual network call.
const SESSION_CHECK_TIMEOUT_MS = 15000;
// FIX: Retry budget after a primary timeout. A short second window covers the
// common "slow cold start, then fine" case without blocking the user long.
const SESSION_RETRY_TIMEOUT_MS  = 5000;
const SHOW_CONTINUE_BUTTON_MS   = 5000;

// FIX: On cold start (app was closed when notification tapped), Capacitor fires
// the deep link via getLaunchUrl() — NOT via appUrlOpen.
const BOOT_BLOCKED_PATHS = new Set([
  'boot', 'splash', 'auth', 'auth/callback', 'native-callback', 'oauth/auth',
]);

async function tryHandleOAuthDeeplink(url: string): Promise<boolean> {
  try {
    const schemes = ['gistgift://', 'gistandgift://'];
    if (!schemes.some(s => url.startsWith(s))) return false;

    const normalized = 'https://app.local/' + url.replace(/^(gistgift|gistandgift):\/\//, '');
    const parsed     = new URL(normalized);
    const path       = parsed.pathname.replace(/^\/+/, '');

    if (path !== 'oauth/auth') return false;

    const accessToken  = parsed.searchParams.get('access_token');
    const refreshToken = parsed.searchParams.get('refresh_token');

    if (!accessToken || !refreshToken) return false;

    console.log('[Boot] OAuth return deeplink on cold start — setting session');
    const { error } = await supabase.auth.setSession({
      access_token:  accessToken,
      refresh_token: refreshToken,
    });

    if (error) {
      console.error('[Boot] OAuth setSession failed:', error.message);
      return false;
    }

    console.log('[Boot] OAuth cold-start session set successfully');
    return true;
  } catch (e) {
    console.warn('[Boot] tryHandleOAuthDeeplink failed:', e);
    return false;
  }
}

async function getNotificationLaunchTarget(isNative: boolean): Promise<string | null> {
  if (!isNative) return null;
  try {
    const { url } = await CapacitorApp.getLaunchUrl();
    if (!url) return null;

    const schemes = ['gistgift://', 'gistandgift://'];
    if (!schemes.some((s) => url.startsWith(s))) return null;

    const normalized = 'https://app.local/' + url.replace(/^(gistgift|gistandgift):\/\//, '');
    const parsed = new URL(normalized);
    const path   = parsed.pathname.replace(/^\/+/, '');
    const scanId = parsed.searchParams.get('scanId');

    if (BOOT_BLOCKED_PATHS.has(path) || path.startsWith('oauth/')) return null;

    if (scanId && (path === 'results' || path === '')) {
      return `/results?scanId=${scanId}`;
    }
    if (path && path !== 'home') {
      return `/${path}`;
    }
  } catch (e) {
    console.warn('[Boot] getLaunchUrl failed:', e);
  }
  return null;
}

const GUEST_MODE_KEY = 'gistgift_guest_mode';

// FIX: Read guest mode from localStorage (persistent) with sessionStorage fallback
// for migration from the old volatile storage.
function isGuestMode(): boolean {
  return localStorage.getItem(GUEST_MODE_KEY) === '1' ||
         sessionStorage.getItem(GUEST_MODE_KEY) === '1';
}

interface BootState {
  stage: "initializing" | "checking-session" | "redirecting" | "error" | "offline";
  message: string;
  error?: Error;
}

function checkOnlineStatus(): boolean {
  return navigator.onLine;
}

function hasValidSubClaim(accessToken?: string | null): boolean {
  if (!accessToken) return false;
  try {
    const payloadPart = accessToken.split('.')[1];
    if (!payloadPart) return false;
    const normalized = payloadPart.replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(normalized));
    return typeof payload?.sub === 'string' && payload.sub.length > 0;
  } catch {
    return false;
  }
}

async function clearCorruptSession(): Promise<void> {
  try {
    await Promise.race([
      supabase.auth.signOut(),
      new Promise((resolve) => setTimeout(resolve, 2000)),
    ]);
  } catch {
    // Ignore sign-out errors; clear storage fallback below.
  }

  try {
    for (const key of Object.keys(localStorage)) {
      if (key.startsWith('sb-') && key.endsWith('-auth-token')) {
        localStorage.removeItem(key);
      }
    }
  } catch {
    // Ignore storage failures.
  }
}

export default function Boot() {
  const navigate = useNavigate();
  const native = useMemo(() => isNativeRuntime(), []);
  const [state, setState] = useState<BootState>({
    stage: "initializing",
    message: "Starting GistGift\u2026",
  });
  const [showContinue, setShowContinue] = useState(false);
  // FIX: Delay showing any visible UI by 800ms. The happy path (session found,
  // guest mode, or quick redirect) completes in <500ms — rendering a spinner
  // and broken logo image for that brief flash is jarring and confusing.
  const [showUI, setShowUI] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setShowUI(true), 800);
    return () => clearTimeout(t);
  }, []);
  const [isOnline, setIsOnline] = useState(checkOnlineStatus());
  const hasNavigated = useRef(false);
  const abortController = useRef<AbortController | null>(null);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      if (state.stage === "offline") {
        setState({ stage: "initializing", message: "Connection restored\u2026" });
      }
    };
    const handleOffline = () => {
      setIsOnline(false);
      setState({ stage: "offline", message: "No internet connection" });
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [state.stage]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (state.stage !== "error" && state.stage !== "redirecting") {
        setShowContinue(true);
      }
    }, SHOW_CONTINUE_BUTTON_MS);

    return () => clearTimeout(timer);
  }, [state.stage]);

  const navigateWithGuard = useCallback(
    (path: string, preserveParams = false) => {
      if (hasNavigated.current) return;
      hasNavigated.current = true;

      const params = preserveParams ? window.location.search + window.location.hash : "";
      navigate(path + params, { replace: true });
    },
    [navigate]
  );

  const performBootSequence = useCallback(async () => {
    if (!isOnline) {
      setState({ stage: "offline", message: "No internet connection" });
      return;
    }

    try {
      abortController.current = new AbortController();

      // FIX: Cold-start OAuth return — check the Capacitor launch URL for an
      // OAuth deeplink with tokens BEFORE doing anything else.
      if (native) {
        try {
          const { url: launchUrl } = await CapacitorApp.getLaunchUrl();
          if (launchUrl) {
            const oauthHandled = await tryHandleOAuthDeeplink(launchUrl);
            if (oauthHandled) {
              setState({ stage: "redirecting", message: "Welcome!" });
              navigateWithGuard("/home");
              return;
            }
          }
        } catch (e) {
          console.warn('[Boot] Cold-start OAuth check failed:', e);
        }
      }

      const urlParams  = new URLSearchParams(window.location.search);
      const hashParams = new URLSearchParams(window.location.hash.substring(1));

      const hasAccessToken = urlParams.has("access_token") || hashParams.has("access_token");
      const hasOAuthCode   = urlParams.has("code") && urlParams.has("state");
      const hasOAuthError  = urlParams.has("error") || hashParams.has("error");

      if (hasAccessToken || hasOAuthCode || hasOAuthError) {
        console.log("[Boot] OAuth callback detected, routing to /auth");
        setState({ stage: "redirecting", message: "Processing authentication\u2026" });
        navigateWithGuard("/auth", true);
        return;
      }

      setState({ stage: "checking-session", message: "Checking your session\u2026" });

      // FIX: Retry logic for session check timeouts.
      // Primary attempt uses 15s; on timeout, retries once with 5s.
      // If both fail, navigate to /home rather than dead-ending on an error screen.
      const trySession = (timeoutMs: number) =>
        Promise.race([
          supabase.auth.getSession(),
          new Promise<never>((_, reject) =>
            setTimeout(() => reject(new Error("Session check timed out")), timeoutMs)
          ),
        ]);

      let sessionCheckData: Awaited<ReturnType<typeof supabase.auth.getSession>>['data'] | null = null;
      let timedOut = false;

      try {
        const { data } = await trySession(SESSION_CHECK_TIMEOUT_MS);
        sessionCheckData = data as any;
      } catch (primaryErr) {
        const isTimeout =
          primaryErr instanceof Error && primaryErr.message.includes("timed out");
        if (!isTimeout) throw primaryErr; // Non-timeout errors go to outer catch

        console.warn("[Boot] Primary session check timed out — retrying with 5s window");
        setState({ stage: "checking-session", message: "Still loading\u2026" });

        try {
          const { data } = await trySession(SESSION_RETRY_TIMEOUT_MS);
          sessionCheckData = data as any;
        } catch {
          timedOut = true;
        }
      }

      if (abortController.current?.signal.aborted) return;

      // FIX: On persistent double-timeout, navigate to /home gracefully.
      // Showing an error screen here was a dead-end — the user almost certainly
      // has a valid cached session and just needs a slow network moment to pass.
      // /home will redirect to /auth itself if there truly is no session.
      if (timedOut || !sessionCheckData) {
        console.warn("[Boot] Session check timed out after retry — navigating to /home");
        setState({ stage: "redirecting", message: "Redirecting\u2026" });
        const notifTarget = await getNotificationLaunchTarget(native);
        navigateWithGuard(notifTarget ?? "/home");
        return;
      }

      let session = (sessionCheckData as any).session;

      if (session && !hasValidSubClaim(session.access_token)) {
        console.warn('[Boot] Corrupt cached session detected (missing sub) — clearing and continuing signed-out');
        await clearCorruptSession();
        session = null;
      }

      // MAINTENANCE MODE: Redirect all users (signed-in, guest, everyone) to /auth
      // which shows the under-construction banner. No one gets into the app.
      if (session?.user || isGuestMode()) {
        console.log('[Boot] Maintenance mode — redirecting to /auth');
        setState({ stage: "redirecting", message: "Redirecting…" });
        navigateWithGuard("/auth");
        return;
      }

      // FIX: Before routing to onboarding/auth, check if this is a notification
      // cold-start. If so, route to the notification target — no account required.
      const coldStartNotifTarget = await getNotificationLaunchTarget(native);
      if (coldStartNotifTarget) {
        console.log('[Boot] No session but notification launch target found:', coldStartNotifTarget);
        setState({ stage: "redirecting", message: "Opening notification..." });
        navigateWithGuard(coldStartNotifTarget);
        return;
      }

      const hasCompletedOnboarding = localStorage.getItem("onboarding_completed") === "true";

      if (!hasCompletedOnboarding) {
        setState({ stage: "redirecting", message: "Let\u2019s get started\u2026" });
        navigateWithGuard("/onboarding");
        return;
      }

      setState({ stage: "redirecting", message: "Redirecting\u2026" });
      navigateWithGuard("/auth");
    } catch (error) {
      if (abortController.current?.signal.aborted) return;

      console.error("[Boot] Startup error:", error);

      const guestActive = isGuestMode();
      if (guestActive) {
        console.log('[Boot] Session check failed but guest mode active — routing to /home');
        setState({ stage: "redirecting", message: "Welcome back!" });
        const notifTarget = await getNotificationLaunchTarget(native);
        navigateWithGuard(notifTarget ?? "/home");
        return;
      }

      setState({
        stage: "error",
        message: error instanceof Error ? error.message : "Failed to start app",
        error: error instanceof Error ? error : new Error("Unknown error"),
      });
    }
  }, [isOnline, navigateWithGuard, native]);

  useEffect(() => {
    performBootSequence();

    return () => {
      abortController.current?.abort();
    };
  }, [performBootSequence]);

  const handleRestart = useCallback(() => {
    window.location.reload();
  }, []);

  const handleContinueToAuth = useCallback(() => {
    const guestActive = isGuestMode();
    if (guestActive) {
      console.log('[Boot] Continue Anyway — guest mode active, routing to /home');
      navigateWithGuard("/home");
    } else {
      navigateWithGuard("/auth");
    }
  }, [navigateWithGuard]);

  const handleReset = useCallback(() => {
    localStorage.clear();
    sessionStorage.clear();
    window.location.reload();
  }, []);

  // FIX: If UI isn't ready yet, render just the background — no logo, no spinner.
  // This prevents the jarring flash of broken image + spinner on fast redirects.
  if (!showUI) {
    return (
      <main
        className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5"
        style={{
          paddingTop: "calc(1.5rem + var(--safe-area-top, 0px))",
          paddingBottom: "calc(1.5rem + var(--safe-area-bottom, 0px))",
        }}
      />
    );
  }

  return (
    <main
      className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5 flex items-center justify-center p-6 transition-colors duration-500"
      style={{
        paddingTop: "calc(1.5rem + var(--safe-area-top, 0px))",
        paddingBottom: "calc(1.5rem + var(--safe-area-bottom, 0px))",
      }}
    >
      <div className="w-full max-w-sm">
        <div className="text-center space-y-4 mb-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
          <div className="relative inline-block">
            <img
              src="/logo.webp"
              alt="GistGift"
              className="w-24 h-24 mx-auto rounded-2xl shadow-lg"
              onError={(e) => {
                (e.target as HTMLImageElement).src = "/logo.png";
              }}
            />
            {state.stage === "checking-session" && (
              <div className="absolute -bottom-2 -right-2 bg-primary rounded-full p-2 shadow-lg animate-pulse">
                <Loader2 className="w-4 h-4 text-primary-foreground animate-spin" />
              </div>
            )}
          </div>
          <h1 className="text-3xl font-bold text-foreground tracking-tight">GistGift</h1>
        </div>

        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-6 duration-700 delay-150">
          {state.stage === "offline" && (
            <div className="bg-card rounded-2xl border border-border shadow-sm p-6 space-y-4">
              <div className="flex items-center justify-center">
                <div className="rounded-full bg-destructive/10 p-3">
                  <WifiOff className="w-6 h-6 text-destructive" />
                </div>
              </div>
              <div className="text-center space-y-2">
                <p className="font-medium text-foreground">No Internet Connection</p>
                <p className="text-sm text-muted-foreground">
                  Please check your connection and try again
                </p>
              </div>
              <Button onClick={handleRestart} className="w-full" size="lg">
                Retry
              </Button>
            </div>
          )}

          {state.stage === "error" && (
            <div className="bg-card rounded-2xl border border-destructive/20 shadow-sm p-6 space-y-4">
              <div className="text-center space-y-2">
                <p className="font-medium text-destructive">Something went wrong</p>
                <p className="text-sm text-muted-foreground">{state.message}</p>
              </div>
              <div className="flex gap-3">
                <Button onClick={handleRestart} className="flex-1" size="lg">
                  Restart
                </Button>
                <Button onClick={handleReset} variant="outline" className="flex-1" size="lg">
                  Reset App
                </Button>
              </div>
            </div>
          )}

          {(state.stage === "initializing" ||
            state.stage === "checking-session" ||
            state.stage === "redirecting") && (
            <div className="space-y-4">
              <div className="flex items-center justify-center gap-3">
                {isOnline ? (
                  <Wifi className="w-4 h-4 text-muted-foreground" />
                ) : (
                  <WifiOff className="w-4 h-4 text-muted-foreground" />
                )}
                <Loader2 className="w-5 h-5 text-primary animate-spin" />
              </div>

              <p className="text-center text-sm text-muted-foreground animate-pulse">
                {state.message}
              </p>

              <div className="flex justify-center gap-2">
                {[0, 1, 2].map((i) => (
                  <div
                    key={i}
                    className="w-2 h-2 rounded-full bg-primary/30 animate-pulse"
                    style={{ animationDelay: `${i * 200}ms` }}
                  />
                ))}
              </div>

              {showContinue && (
                <div className="mt-8 pt-6 border-t border-border space-y-3 animate-in fade-in slide-in-from-bottom-4 duration-500">
                  <p className="text-xs text-center text-muted-foreground">
                    Taking longer than expected?
                  </p>
                  <Button
                    onClick={handleContinueToAuth}
                    variant="outline"
                    className="w-full"
                    size="lg"
                  >
                    Continue Anyway
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="mt-12 text-center text-xs text-muted-foreground animate-in fade-in duration-1000 delay-300">
          {native ? "Native App" : "Web App"} • v1.0.0
        </div>
      </div>
    </main>
  );
}